const {
  GRAPHQL_ENDPOINT,
  MICROSERVICES_URL,
  NODE_ENV,
  SENTRY_URL,
  BABYLON_ENVIRONMENT,
  PRODUCT_CONFIG_URL,
  REACT_APP_VERSION,
  AUTH0_DOMAIN_URL,
  AUTH0_CLIENT_ID,
  BASE_URL,
  TENANCY_URL,
  GATEKEEPER_BASE_URL,
  PLATFORM_GATEWAY_URL,
} = window.babylonConfig || {}

type Region = 'us' | 'uk' | 'ca'
type Env = 'prod' | 'preprod' | 'staging' | 'dev'

const authDictionary = {
  prod: {
    ca: {}, // does not exist
    us: {
      domain: 'auth.global2.babylonpartners.com',
      id: 'r93KTOnUpUphgWBDqg8hHr9VUrT5aAIl',
    },
    uk: {
      domain: 'auth.global1.babylonpartners.com',
      id: 'ueYz9hqLCg3P4Coc7LQvqHpqaBDNWDaa',
    },
  },
  preprod: {
    ca: {
      domain: 'auth.global2.preprod.babylontech.co.uk',
      id: '0LV0G7WAcrZ2FVorb3xc1jFr9vTAR3lF',
    },
    us: {
      domain: 'auth.global2.preprod.babylontech.co.uk',
      id: '3l2SWou60mUyfW63w3o10mQQLaxPsiz8',
    },
    uk: {
      domain: 'auth.global1.preprod.babylontech.co.uk',
      id: 'iUMYcaMzboGVgQ8W2C4fGJ8SJmmTsyGN',
    },
  },
  staging: {
    ca: {}, // does not exist
    us: {
      domain: 'auth.global1.staging.babylontech.co.uk',
      id: 'Bze0EK04RmX0zTCcZgKePOE8NqGBml6c',
    },
    uk: {
      domain: 'auth.global1.staging.babylontech.co.uk',
      id: '9Q4bqR2yTVi8111BHatp63f7IsryUPQl',
    },
  },
  dev: {
    ca: {}, // does not exist
    us: {
      domain: 'auth.global1.dev.babylontech.co.uk',
      id: 'y06y2N6sIWcWqsF765ted4XpBzfawYKb',
    },
    uk: {
      domain: 'auth.global1.dev.babylontech.co.uk',
      id: 'PgIABRBzsm9aggKYvDVJbY1n6qSxsuoh',
    },
  },
}

const getAuth0Info = (
  region: Region,
  env: Env
): { domain: string; id: string } => {
  const auth = authDictionary[env][region]

  if (Object.keys(auth).length === 0) {
    console.warn(
      'There is no auth0 environment matching that input. Defaulting to dev-uk'
    )

    return authDictionary.dev.uk
  }

  // @ts-ignore
  return auth
}

// domains in US use .us format, whereas in UK they us -uk format.
const getPrefix = (region: Region) => {
  switch (region) {
    case 'ca':
    case 'us':
      return '.'
    case 'uk':
    default:
      return '-'
  }
}

const getHostName = (prefix: string, region: Region, env: Env) => {
  const babylonEnv = !env.startsWith('auth_qa_staging') ? env : 'staging'
  const regionSubdomain = region

  switch (babylonEnv) {
    case 'prod':
      switch (region) {
        case 'ca':
        case 'us':
          return `.${regionSubdomain}.babylonpartners.com`
        default:
        case 'uk':
          return '.babylonpartners.com'
      }
    default:
      return `${prefix}${regionSubdomain}.${babylonEnv}.babylontech.co.uk`
  }
}

const getDomain = (region: Region, env: Env) => {
  const babylonRegion = region === 'us' && env === 'dev' ? 'uk' : region
  const prefix = getPrefix(babylonRegion)

  return getHostName(prefix, babylonRegion, env)
}

const parseEnv = (input: string): { region: Region; env: Env } => {
  const parsed = input.split('.')

  if (parsed.length > 1) {
    return {
      region: parsed[0] as Region,
      env: parsed[1] as Env,
    }
  }

  return {
    region: 'uk',
    env: parsed[0] as Env,
  }
}

const { region, env } = parseEnv(process.env.REACT_APP_ENV || 'dev')
const baseUrl = getDomain(region, env)
const { domain, id } = getAuth0Info(region, env)

export default {
  GRAPHQL_ENDPOINT:
    GRAPHQL_ENDPOINT ||
    (NODE_ENV === 'production'
      ? '/graphql'
      : 'https://localhost.dev.babylontech.co.uk:6443/graphql'),
  IS_SERVER: Boolean(window.isServer),
  MICROSERVICES_URL: MICROSERVICES_URL || `https://services${baseUrl}`,
  SENTRY_URL: SENTRY_URL || '',
  // TODO: Find a better way of doing this.
  VERSION: window.Cypress
    ? 'cypress'
    : REACT_APP_VERSION || process.env.REACT_APP_VERSION,
  BABYLON_ENVIRONMENT: BABYLON_ENVIRONMENT || env,
  PRODUCT_CONFIG_URL,
  AUTH0_DOMAIN_URL: AUTH0_DOMAIN_URL || domain,
  AUTH0_CLIENT_ID: AUTH0_CLIENT_ID || id,
  BASE_URL: BASE_URL || 'https://localhost.dev.babylontech.co.uk:3000',
  TENANCY_URL:
    TENANCY_URL ||
    'https://services.global1.dev.babylontech.co.uk/tenancy-authority/v1/partners',
  GATEKEEPER_BASE_URL:
    GATEKEEPER_BASE_URL || `https://services${baseUrl}/gatekeeper`,
  PLATFORM_GATEWAY_URL:
    PLATFORM_GATEWAY_URL || `https://platform${baseUrl}/graph`,
}
